import {
  buildLiveActivitySnapshot,
  liveActivityPickPoints,
  type LiveActivityPick,
  type LiveActivityPotw,
  type LiveActivitySnapshot,
  type LiveActivityWeekGame,
} from '@shared/liveActivity';
import { isHeadToHeadProp, orderPlayersForSides } from '@shared/propDisplay';
import type { LegacyScoringSystem } from '@shared/scoring';

type WeekGameGroup = {
  game?: {
    id?: string;
    gameTime?: string | Date | null;
    isCompleted?: boolean | null;
    homeScore?: number | null;
    awayScore?: number | null;
    homeLineScores?: unknown;
    period?: string | null;
    clock?: string | null;
    status?: string | null;
    liveStatus?: string | null;
    homeTeam?: { abbreviation?: string | null } | null;
    awayTeam?: { abbreviation?: string | null } | null;
  } | null;
  bets?: {
    id?: string;
    leagueWeekBetId?: string | null;
    betType?: string | null;
    oddsOne?: number | null;
    oddsTwo?: number | null;
    propDefinitionId?: string | null;
    displayInfo?: Record<string, unknown> | null;
    isPropOfTheWeek?: boolean | null;
    scoringConfigOverride?: Record<string, unknown> | null;
  }[] | null;
};

type PickRow = {
  picks?: {
    gameId?: string | null;
    side?: string | null;
    betType?: string | null;
    leagueWeekBetId?: string | null;
  } | null;
};

type PropDefinition = {
  id?: string;
  nflGameId?: string | null;
  propType?: string | null;
  statType?: string | null;
  threshold?: number | string | null;
  displayConfig?: Record<string, unknown> | null;
  players?: { id?: string; fullName?: string | null; teamAbbr?: string | null }[] | null;
};

type PropEvaluation = {
  propDefinitionId?: string;
  currentValues?: unknown;
};

const STAT_LABELS: Record<string, string> = {
  PLAYER_OVER_UNDER_YARDS: 'rush yds',
  PLAYER_OVER_UNDER_RECEPTIONS: 'rec',
  PLAYER_OVER_UNDER_CARRIES: 'carries',
  PLAYER_OVER_UNDER_INTERCEPTIONS: 'int',
  PLAYER_OVER_UNDER_SACKS: 'sacks',
  PLAYER_VS_PLAYER_YARDS: 'yds',
  ANYTIME_TD: 'TDs',
  RUSHING_TD: 'rush TDs',
  RECEIVING_TD: 'rec TDs',
  PASSING_TD: 'pass TDs',
  LONGEST_STAT: 'long',
};

const STAT_TYPE_LABELS: Record<string, string> = {
  passing_long: 'Longest pass',
  rushing_long: 'Longest rush',
  receptions_long: 'Longest reception',
  passing_yards: 'Passing yards',
  rushing_yards: 'Rushing yards',
  receptions_yards: 'Receiving yards',
};

const parseNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const numericRecord = (value: unknown): Record<string, number> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter((entry): entry is [string, number] => {
      const [, entryValue] = entry;
      return typeof entryValue === 'number' && Number.isFinite(entryValue);
    }),
  );
};

const isGamePick = (betType: string | null | undefined): betType is LiveActivityPick['betType'] =>
  betType === 'moneyline' || betType === 'spread' || betType === 'total';

export function snapshotFromLeagueWeek(input: {
  now: Date;
  games: WeekGameGroup[] | null | undefined;
  picks: PickRow[] | null | undefined;
  propDefinitions?: PropDefinition[] | null;
  propEvaluations?: PropEvaluation[] | null;
  scoringSystem?: LegacyScoringSystem | null;
}): LiveActivitySnapshot | null {
  const groups = input.games ?? [];
  const games: LiveActivityWeekGame[] = [];
  const potwBet = groups
    .flatMap((group) => group.bets ?? [])
    .find((bet) => bet.isPropOfTheWeek === true || bet.scoringConfigOverride?.propOfTheWeek === true);

  for (const group of groups) {
    const game = group.game;
    if (!game?.id || !game.gameTime) continue;
    const gameTime = game.gameTime instanceof Date ? game.gameTime.toISOString() : String(game.gameTime);
    games.push({
      id: game.id,
      gameTime,
      isCompleted: game.isCompleted === true,
      homeScore: typeof game.homeScore === 'number' ? game.homeScore : null,
      awayScore: typeof game.awayScore === 'number' ? game.awayScore : null,
      homeAbbreviation: game.homeTeam?.abbreviation ?? '',
      awayAbbreviation: game.awayTeam?.abbreviation ?? '',
      homeLineScores: game.homeLineScores,
      period: game.period ?? null,
      clock: game.clock ?? null,
      status: game.status ?? null,
      liveStatus: game.liveStatus ?? null,
    });
  }

  const picks: LiveActivityPick[] = [];
  for (const row of input.picks ?? []) {
    const pick = row.picks;
    if (!pick?.gameId || !isGamePick(pick.betType) || (pick.side !== 'one' && pick.side !== 'two')) continue;
    const group = groups.find((entry) => entry.game?.id === pick.gameId);
    const bet = (group?.bets ?? []).find((entry) =>
      (entry.leagueWeekBetId ?? entry.id) === pick.leagueWeekBetId,
    ) ?? (group?.bets ?? []).find((entry) => entry.betType === pick.betType);
    const side = pick.side === 'one' || pick.side === 'two' ? pick.side : null;
    picks.push({
      gameId: pick.gameId,
      betType: pick.betType,
      side: pick.side,
      spread: parseNumber(bet?.displayInfo?.spread),
      total: parseNumber(bet?.displayInfo?.total),
      points: side
        ? liveActivityPickPoints({
            scoringSystem: input.scoringSystem,
            betType: pick.betType,
            side,
            oddsOne: bet?.oddsOne,
            oddsTwo: bet?.oddsTwo,
            displayInfo: bet?.displayInfo ?? null,
            scoringConfigOverride: bet?.scoringConfigOverride ?? null,
          })
        : null,
    });
  }

  let potw: LiveActivityPotw | null = null;
  if (potwBet?.propDefinitionId) {
    const definition = (input.propDefinitions ?? []).find((entry) => entry.id === potwBet.propDefinitionId);
    const evaluation = (input.propEvaluations ?? []).find((entry) => entry.propDefinitionId === potwBet.propDefinitionId);
    const players = orderPlayersForSides(
      (definition?.players ?? []).filter((player): player is { id: string; fullName: string; teamAbbr?: string | null } =>
        Boolean(player.id && player.fullName),
      ),
      definition?.displayConfig,
    );
    const values = numericRecord(evaluation?.currentValues);
    const line = parseNumber(definition?.threshold);
    const potwPick = (input.picks ?? []).find((row) =>
      (row.picks?.leagueWeekBetId ?? '') === (potwBet.leagueWeekBetId ?? potwBet.id),
    );
    const gameId = definition?.nflGameId ?? groups.find((group) =>
      (group.bets ?? []).some((bet) => bet.propDefinitionId === potwBet.propDefinitionId),
    )?.game?.id;
    const displayLabel = typeof definition?.displayConfig?.['label'] === 'string'
      ? definition.displayConfig['label'].trim()
      : '';
    const matchup = isHeadToHeadProp(definition?.propType ?? '', players.length);
    const first = players[0];
    const second = players[1];
    if (gameId && matchup && first?.fullName && second?.fullName) {
      potw = {
        gameId,
        kind: 'matchup',
        playerName: first.fullName,
        playerTwoName: second.fullName,
        playerOneAbbr: first.teamAbbr ?? '',
        playerTwoAbbr: second.teamAbbr ?? '',
        statLabel: displayLabel || STAT_TYPE_LABELS[definition?.statType ?? ''] || STAT_LABELS[definition?.propType ?? ''] || 'Matchup',
        current: first.id ? values[first.id] ?? 0 : 0,
        playerTwoValue: second.id ? values[second.id] ?? 0 : 0,
        line: line ?? 0,
        side: potwPick?.picks?.side === 'one' || potwPick?.picks?.side === 'two' ? potwPick.picks.side : null,
      };
    } else if (gameId && first?.fullName && line !== null) {
      potw = {
        gameId,
        playerName: first.fullName,
        statLabel: STAT_LABELS[definition?.propType ?? ''] ?? 'line',
        current: first.id ? values[first.id] ?? 0 : 0,
        line,
        side: potwPick?.picks?.side === 'one' || potwPick?.picks?.side === 'two' ? potwPick.picks.side : 'one',
      };
    }
  }

  return buildLiveActivitySnapshot({ now: input.now, games, picks, potw });
}
